import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cn } from './cn.js';
const BAR_CLASSES = {
    primary: 'bg-primary',
    muted: 'bg-muted-foreground/40',
    success: 'bg-success',
    danger: 'bg-destructive',
    onPrimary: 'bg-primary-hero-trend',
};
const MIN_BAR_PCT = 6;
function heroTrendPath(data, height) {
    const max = data.length ? Math.max(...data, 0) : 0;
    const points = data.length ? data : [0];
    const last = points.length - 1;
    return points
        .map((value, index) => {
        const x = last > 0 ? (index / last) * 100 : 50;
        const y = max > 0 ? height - (Math.max(value, 0) / max) * (height - 4) - 2 : height - 2;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
        .join(' ');
}
/**
 * Sparkline for KPI tiles — bars by default, a thin light line on filled hero tiles.
 * Decorative: the real value lives in the tile's number, so the chart is hidden from assistive tech.
 */
export const Sparkline = forwardRef(({ className, data, tone = 'muted', height = 32, ...props }, ref) => {
    const max = data.length ? Math.max(...data, 0) : 0;
    if (tone === 'onPrimary') {
        const path = heroTrendPath(data, height);
        return (_jsx("div", { ref: ref, "aria-hidden": "true", className: cn('overflow-hidden', className), style: { height }, ...props, children: _jsxs("svg", { className: "h-full w-full", viewBox: `0 0 100 ${height}`, preserveAspectRatio: "none", children: [_jsx("path", { d: `${path} L 100 ${height} L 0 ${height} Z`, className: "fill-primary-hero-trend-fill" }), _jsx("path", { d: path, className: "stroke-primary-hero-trend", fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "1.5", vectorEffect: "non-scaling-stroke" })] }) }));
    }
    return (_jsx("div", { ref: ref, "aria-hidden": "true", className: cn('flex items-end gap-[3px]', className), style: { height }, ...props, children: data.map((v, i) => {
            const pct = max > 0 ? Math.max((Math.max(v, 0) / max) * 100, MIN_BAR_PCT) : MIN_BAR_PCT;
            return (_jsx("span", { className: cn('min-w-[2px] flex-1 rounded-sm', BAR_CLASSES[tone]), style: { height: `${pct}%` } }, i));
        }) }));
});
Sparkline.displayName = 'Sparkline';
